import React, { useMemo } from 'react'
import { useGetConvenience } from 'services/places'
import LoadingScreen from 'components/Layout/LoadingScreen'
import {
  CompanyPageLayout,
  CompanyContentSections,
} from 'components/NewCompanyPage'
import { DoorsModal } from 'components/NewCompanyPage/Modals/DoorsModal/DoorsModal'
import useDocumentTitle from 'components/useDocumentTitle'
import { ConvenienceDoorTable } from 'components/DataExpansionComponents/GooglePlaces/ConvenienceDoors/ConvenienceDoorsTable/ConvenienceDoorsTable'
import { CONVENIENCE_DOOR_TABLE_KEY } from 'constants/tableQueryKeys'
import { buildConveniencePageContent } from './companyTypeData'

export function NewConveniencePage({
  convenienceID,
}: {
  convenienceID?: number
}) {
  const { data, isLoading } = useGetConvenience(convenienceID)

  const [showDoorsModal, setShowDoorsModal] = React.useState(false)

  // Shared derivation — identical stats/fields are reused by MobileConveniencePage.
  const content = buildConveniencePageContent(data, {
    onViewDoors: () => setShowDoorsModal(true),
  })

  useDocumentTitle(isLoading ? 'Loading...' : content.documentTitle)

  const entityBreadcrumbs = useMemo(
    () =>
      content.header.name
        ? [{ title: 'Company', label: content.header.name }]
        : [],
    [content.header.name]
  )

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <>
      <CompanyPageLayout
        chainProxyId={data!.id!}
        breadcrumbRoot={content.breadcrumbRoot}
        breadcrumbs={[
          {
            title: content.breadcrumbTitle,
            breadcrumb: content.header.name,
            url: undefined,
          },
        ]}
        entityBreadcrumbs={entityBreadcrumbs}
        categoryInfo={content.categoryInfo}
        sidebar={{
          header: {
            name: content.header.name,
            domain: content.header.domain ?? '',
            logoUrl: content.header.logoUrl,
          },
          tabs: [
            {
              key: 'summary',
              title: 'Summary',
              content: content.summaryContent,
            },
            {
              key: 'location-details',
              title: 'Location Details',
              content: content.locationDetailsContent,
            },
          ],
        }}
      >
        <CompanyContentSections
          chainProxyId={data!.id!}
          entityID={convenienceID}
          companyName={content.header.name}
          websiteAddress={content.header.domain ?? ''}
          companyTypeSlug={content.companyTypeSlug}
          tableTypeSlug={content.tableTypeSlug}
        />
      </CompanyPageLayout>

      {/* Doors Modal with Tabs */}
      {data && (
        <DoorsModal
          show={showDoorsModal}
          onHide={() => setShowDoorsModal(false)}
          forChainProxyId={data.id}
          chainData={{
            id: data.id,
            name: content.header.name,
            domain: content.header.domain,
            count: data.cstore?.doors_cnt,
          }}
          chainName={content.header.name}
          subCategory={content.modalSubCategory}
          matchedTableComponent={ConvenienceDoorTable}
          matchedTableKey={`${CONVENIENCE_DOOR_TABLE_KEY}-${data.id}`}
          matchedTableProps={{
            hideCreateCompanyButton: true,
            baseFilters: { chain_proxy_id: data.id },
          }}
          unmatchedTableKeyPrefix="unmatched-convenience-doors-table"
        />
      )}
    </>
  )
}
